import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Search } from 'lucide-react';
import Status from '@/pages/Submissions/components/pizza_tracker_tabs/Status';
import { useSubmission } from '@/hooks/submissions/useSubmission';
import {
  useSubmissionActivities,
  type HubAttributedUser,
  type SubmissionActivityUser,
  type SubmissionActivity,
} from '@/hooks/submissions/useSubmissionActivities';
import { Skeleton } from '@/components/ui/skeleton';

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

/**
 * Activity row attribution — prefers full name, falls back to email so the
 * row never reads `by —` when at least one identity field is populated.
 */
const formatActivityActor = (
  user: SubmissionActivityUser | null | undefined,
  hubAttributed?: HubAttributedUser | null
): string | null => {
  if (user) {
    const name = [user.first_name, user.last_name]
      .map(part => part?.trim() ?? '')
      .filter(Boolean)
      .join(' ');
    if (name) return name;
    const email = user.email_address?.trim() ?? '';
    return email || null;
  }
  const hubName = hubAttributed?.display_name?.trim() ?? '';
  return hubName || null;
};

/**
 * When an activity has a document_id in metadata, find the quoted filename
 * in the details string and render it as a link to the Documents tab.
 * Returns the details as plain text if no document link is needed.
 */
const ActivityDetails = ({
  activity,
  documentsHref,
}: {
  activity: SubmissionActivity;
  documentsHref: string;
}) => {
  const documentId = activity.metadata?.document_id;
  if (!documentId) return <span className="text-gray-900">{activity.details}</span>;

  const match = activity.details.match(/^(.*?")(.*?)(".*)/);
  if (!match) return <span className="text-gray-900">{activity.details}</span>;

  const [, before, filename, after] = match;
  const href = `${documentsHref}&highlight=${documentId}`;
  return (
    <span className="text-gray-900">
      {before}
      <Link
        to={href}
        className="text-primary underline hover:text-primary/80"
        onClick={e => e.stopPropagation()}
      >
        {filename}
      </Link>
      {after}
    </span>
  );
};

const Activity = ({ submissionId }: { submissionId: string }) => {
  const [search, setSearch] = useState('');
  const location = useLocation();
  const documentsHref = `${location.pathname}?tab=documents`;
  const { submission, isLoading: submissionLoading } =
    useSubmission(submissionId);
  const { activities, isLoading: activitiesLoading } =
    useSubmissionActivities(submissionId);

  if (submissionLoading || !submission) {
    return <div>Loading...</div>;
  }

  const filtered = activities.filter(a =>
    a.details.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="grid grid-cols-3 gap-6">
      {/* Recent Activity */}
      <div className="col-span-2 bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-gray-900 text-xl">Recent Activity</h2>
          <div className="relative w-64">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white text-sm"
            />
          </div>
        </div>
        <div className="max-h-[600px] overflow-y-auto pr-4">
          {activitiesLoading ? (
            <div className="space-y-0">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="py-4 border-b border-gray-100">
                  <Skeleton className="h-4 w-48 mb-2" />
                  <Skeleton className="h-3 w-full" />
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-gray-500">No activity found.</p>
          ) : (
            <div className="space-y-0">
              {filtered.map(activity => {
                const actor = formatActivityActor(
                  activity.user,
                  activity.hub_attributed_user
                );
                return (
                  <div
                    key={activity.id}
                    className="py-4 border-b border-gray-100 last:border-0 -mx-4 px-4"
                  >
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <ActivityDetails activity={activity} documentsHref={documentsHref} />
                      {actor && (
                        <span className="text-gray-600 text-sm">
                          by {actor}
                        </span>
                      )}
                      <span className="text-gray-400 text-sm">
                        {formatDate(activity.created_at)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Timeline */}
      <div className="col-span-1">
        <Status
          status={submission.status}
          submissionId={submissionId}
          title="Timeline"
          subStatusLabel={submission.sub_status_label}
        />
      </div>
    </div>
  );
};

export default Activity;
